import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Edit3, Save, X, ChevronDown, ChevronUp, LogOut, Users, Plus, Trash2 } from 'lucide-react';

const PRO_TYPES = [
  { key: 'orthophonist', label: 'Orthophoniste' },
  { key: 'psychologist', label: 'Psychologue / Neuropsychologue' },
  { key: 'psychomotor', label: 'Psychomotricien(ne)' },
  { key: 'occupational_therapist', label: 'Ergothérapeute' },
  { key: 'kinesitherapeute', label: 'Kinésithérapeute' },
  { key: 'aba', label: 'ABA Thérapeute' },
  { key: 'osteopathe', label: 'Ostéopathe' },
  { key: 'dieteticien', label: 'Diététicien(ne)' },
  { key: 'sophrologue', label: 'Sophrologie / Relaxation' },
  { key: 'art_therapeute', label: 'Art-thérapeute' },
  { key: 'other_professional', label: 'Autre professionnel' },
];

const Section = ({ title, icon, children, defaultOpen = false, editing, onEdit, onSave, onCancel, saving }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="passerelle-card">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-base">{icon}</span>
          <h3 className="font-heading font-semibold text-slate-700 text-base">{title}</h3>
        </div>
        {open ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
      </button>
      {open && (
        <div className="mt-4 border-t border-stone-50 pt-2">
          {onEdit && (
            <div className="flex justify-end mb-3">
              {!editing ? (
                <button onClick={onEdit} className="flex items-center gap-1 text-sage-600 font-heading font-semibold text-sm">
                  <Edit3 size={13} /> Modifier
                </button>
              ) : (
                <div className="flex gap-3">
                  <button onClick={onCancel} className="flex items-center gap-1 text-slate-400 text-sm font-heading font-semibold">
                    <X size={13} /> Annuler
                  </button>
                  <button onClick={onSave} disabled={saving} className="flex items-center gap-1 text-sage-600 font-heading font-semibold text-sm">
                    {saving ? <div className="w-3 h-3 border border-sage-600 border-t-transparent rounded-full animate-spin" /> : <Save size={13} />}
                    Sauvegarder
                  </button>
                </div>
              )}
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
};

export default function PatientProfil() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const [general, setGeneral] = useState({});
  const [generalEdit, setGeneralEdit] = useState({});
  const [editingGeneral, setEditingGeneral] = useState(false);

  const [treatments, setTreatments] = useState([]);
  const [treatmentsEdit, setTreatmentsEdit] = useState([]);
  const [editingTreatments, setEditingTreatments] = useState(false);

  const [medPros, setMedPros] = useState([]);
  const [medProsEdit, setMedProsEdit] = useState([]);
  const [editingMedPros, setEditingMedPros] = useState(false);

  const [communication, setCommunication] = useState({});
  const [communicationEdit, setCommunicationEdit] = useState({});
  const [editingCommunication, setEditingCommunication] = useState(false);

  const [contacts, setContacts] = useState({});
  const [contactsEdit, setContactsEdit] = useState({});
  const [editingContacts, setEditingContacts] = useState(false);

  const [additionalInfo, setAdditionalInfo] = useState({});
  const [additionalInfoEdit, setAdditionalInfoEdit] = useState({});
  const [editingAdditionalInfo, setEditingAdditionalInfo] = useState(false);

  const [weeklySchedule, setWeeklySchedule] = useState([]);
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [scheduleEdit, setScheduleEdit] = useState([]);

  useEffect(() => { if (user) loadData(); }, [user]);

  const loadData = async () => {
    try {
      const { data: patientData } = await supabase.from('patients').select('*').eq('id', user.id).maybeSingle();
      setPatient(patientData);
      setGeneral(patientData || {});

      const [treatRes, prosRes, commRes, contRes, addRes, schedRes] = await Promise.all([
        supabase.from('patient_treatments').select('*').eq('patient_id', user.id).order('created_at'),
        supabase.from('patient_professionals').select('*').eq('patient_id', user.id),
        supabase.from('patient_communication_profile').select('*').eq('patient_id', user.id).maybeSingle(),
        supabase.from('patient_emergency_contacts').select('*').eq('patient_id', user.id).maybeSingle(),
        supabase.from('patient_additional_info').select('*').eq('patient_id', user.id).maybeSingle(),
        supabase.from('patient_weekly_schedule').select('*').eq('patient_id', user.id).order('day_of_week'),
      ]);

      setTreatments(treatRes.data || []);
      setMedPros(prosRes.data || []);
      setCommunication(commRes.data || {});
      setContacts(contRes.data || {});
      setAdditionalInfo(addRes.data || {});
      setWeeklySchedule(schedRes.data || []);
    } catch (error) {
      console.error('Error loading:', error);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 2000); };

  const saveGeneral = async () => {
    setSaving(true);
    try {
      await supabase.from('patients').update(generalEdit).eq('id', user.id);
      setPatient(prev => ({ ...prev, ...generalEdit }));
      setGeneral(prev => ({ ...prev, ...generalEdit }));
      setEditingGeneral(false);
      showMessage('Enregistré !');
    } finally { setSaving(false); }
  };

  const saveTreatments = async () => {
    setSaving(true);
    try {
      await supabase.from('patient_treatments').delete().eq('patient_id', user.id);
      if (treatmentsEdit.length > 0) {
        await supabase.from('patient_treatments').insert(
          treatmentsEdit.map(t => ({ patient_id: user.id, medication_name: t.medication_name || '', dosage: t.dosage || '', frequency: t.frequency || '', prescriber: t.prescriber || '' }))
        );
      }
      const { data } = await supabase.from('patient_treatments').select('*').eq('patient_id', user.id).order('created_at');
      setTreatments(data || []);
      setEditingTreatments(false);
      showMessage('Traitements enregistrés !');
    } finally { setSaving(false); }
  };

  const saveMedPros = async () => {
    setSaving(true);
    try {
      await supabase.from('patient_professionals').delete().eq('patient_id', user.id);
      if (medProsEdit.length > 0) {
        await supabase.from('patient_professionals').insert(
          medProsEdit.map(p => ({ patient_id: user.id, profession: p.profession || '', professional_name: p.professional_name || '', phone: p.phone || '', address: p.address || '', frequency: p.frequency || '', is_on_passerelle: false }))
        );
      }
      const { data } = await supabase.from('patient_professionals').select('*').eq('patient_id', user.id);
      setMedPros(data || []);
      setEditingMedPros(false);
      showMessage('Professionnels enregistrés !');
    } finally { setSaving(false); }
  };

  const saveSection = async (data, table, setData, setEditing) => {
    setSaving(true);
    try {
      const existing = await supabase.from(table).select('id').eq('patient_id', user.id).maybeSingle();
      if (existing.data) { await supabase.from(table).update(data).eq('patient_id', user.id); }
      else { await supabase.from(table).insert({ patient_id: user.id, ...data }); }
      setData(data);
      setEditing(false);
      showMessage('Enregistré !');
    } finally { setSaving(false); }
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      await supabase.from('patient_weekly_schedule').delete().eq('patient_id', user.id);
      if (scheduleEdit.length > 0) {
        await supabase.from('patient_weekly_schedule').insert(
          scheduleEdit.map(s => ({ patient_id: user.id, day_of_week: s.day_of_week, start_time: s.start_time, end_time: s.end_time, label: s.label, category: s.category }))
        );
      }
      const { data } = await supabase.from('patient_weekly_schedule').select('*').eq('patient_id', user.id).order('day_of_week');
      setWeeklySchedule(data || []);
      setEditingSchedule(false);
      showMessage('Emploi du temps enregistré !');
    } finally { setSaving(false); }
  };

  const addTreatment = () => setTreatmentsEdit(prev => [...prev, { id: `new-${Date.now()}`, medication_name: '', dosage: '', frequency: '', prescriber: '' }]);
  const updateTreatment = (index, field, value) => setTreatmentsEdit(prev => prev.map((t, i) => i === index ? {...t, [field]: value} : t));
  const removeTreatment = (index) => setTreatmentsEdit(prev => prev.filter((_, i) => i !== index));

  const addMedPro = () => setMedProsEdit(prev => [...prev, { id: `new-${Date.now()}`, profession: '', professional_name: '', phone: '', address: '', frequency: '' }]);
  const updateMedPro = (index, field, value) => setMedProsEdit(prev => prev.map((p, i) => i === index ? {...p, [field]: value} : p));
  const removeMedPro = (index) => setMedProsEdit(prev => prev.filter((_, i) => i !== index));

  const addScheduleEntry = () => setScheduleEdit(prev => [...prev, { id: `new-${Date.now()}`, day_of_week: 'lundi', start_time: '09:00', end_time: '10:00', label: '', category: 'soin' }]);
  const updateScheduleEntry = (index, field, value) => setScheduleEdit(prev => prev.map((s, i) => i === index ? {...s, [field]: value} : s));
  const removeScheduleEntry = (index) => setScheduleEdit(prev => prev.filter((_, i) => i !== index));
  const duplicateScheduleEntry = (index, targetDays) => {
    const entry = scheduleEdit[index];
    setScheduleEdit(prev => [...prev, ...targetDays.map(day => ({ ...entry, id: `new-${Date.now()}-${day}`, day_of_week: day }))]);
  };

  const handleLogout = async () => { await logout(); navigate('/accueil'); };

  if (loading) return (
    <div className="p-5 space-y-4">{[1,2,3,4].map(i => <div key={i} className="h-20 bg-stone-100 rounded-3xl animate-pulse" />)}</div>
  );

  return (
    <div className="px-5 pt-5 pb-6 space-y-4">

      {/* Header */}
      <div className="passerelle-card text-center py-6" style={{ background: 'linear-gradient(135deg, #EDE9FE 0%, white 60%)' }}>
        <div className="w-20 h-20 rounded-3xl flex items-center justify-center text-white font-heading font-bold text-2xl mx-auto mb-3 shadow-lg" style={{ backgroundColor: '#7C6BAE' }}>
          {patient?.first_name?.[0]}{patient?.last_name?.[0]}
        </div>
        <h1 className="font-heading font-bold text-xl text-slate-800">{patient?.first_name} {patient?.last_name}</h1>
        <p className="text-sm text-slate-400 font-body mt-1">{user?.email}</p>
        <span className="inline-block mt-2 px-3 py-1 rounded-full text-xs font-heading font-semibold" style={{ backgroundColor: '#EDE9FE', color: '#7C6BAE' }}>Patient</span>
      </div>

      {message && (
        <div className="p-3 bg-sage-50 border border-sage-200 rounded-2xl text-sm text-sage-700 font-heading font-semibold text-center">✓ {message}</div>
      )}

      {/* Infos générales */}
      <Section title="Informations générales" icon="👤" defaultOpen
        editing={editingGeneral} onEdit={() => { setGeneralEdit({...general}); setEditingGeneral(true); }}
        onSave={saveGeneral} onCancel={() => setEditingGeneral(false)} saving={saving}>
        {[
          { key: 'first_name', label: 'Prénom' },
          { key: 'last_name', label: 'Nom' },
          { key: 'date_of_birth', label: 'Date de naissance', type: 'date' },
          { key: 'phone', label: 'Téléphone' },
          { key: 'address', label: 'Adresse' },
          { key: 'diagnosis', label: 'Diagnostic / Situation' },
        ].map(({ key, label, type }) => (
          <div key={key} className="py-2.5 border-b border-stone-50 last:border-0">
            <p className="text-xs text-slate-400 font-body mb-1">{label}</p>
            {editingGeneral
              ? <input type={type || 'text'} value={generalEdit[key] || ''} onChange={e => setGeneralEdit(p => ({...p, [key]: e.target.value}))} className="w-full h-9 px-3 bg-stone-50 border border-stone-200 rounded-xl text-sm font-body" />
              : <p className="text-sm text-slate-700 font-body">{general[key] || <span className="text-slate-300 italic">Non renseigné</span>}</p>
            }
          </div>
        ))}
      </Section>

      {/* Emploi du temps */}
      <div className="passerelle-card">
        <button onClick={() => { if (!editingSchedule) setScheduleEdit([...weeklySchedule]); setEditingSchedule(!editingSchedule); }} className="w-full flex items-center justify-between">
          <div className="flex items-center gap-2"><span className="text-base">📅</span><h3 className="font-heading font-semibold text-slate-700 text-base">Emploi du temps</h3></div>
          <ChevronDown size={18} className="text-slate-400" />
        </button>
        {editingSchedule && (
          <div className="mt-4 border-t border-stone-50 pt-4 space-y-3">
            {scheduleEdit.length === 0
              ? <p className="text-sm text-slate-400 font-body italic text-center py-4">Aucun créneau</p>
              : [...scheduleEdit].sort((a, b) => { const o = ['lundi','mardi','mercredi','jeudi','vendredi','samedi','dimanche']; return o.indexOf(a.day_of_week) - o.indexOf(b.day_of_week); }).map((entry) => {
                const index = scheduleEdit.findIndex(e => e.id === entry.id);
                return (
                  <div key={entry.id} className="p-3 bg-stone-50 rounded-2xl space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <select value={entry.day_of_week} onChange={e => updateScheduleEntry(index, 'day_of_week', e.target.value)} className="h-9 px-3 bg-white border border-stone-200 rounded-xl text-sm font-body">
                        {['lundi','mardi','mercredi','jeudi','vendredi','samedi','dimanche'].map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                      </select>
                      <select value={entry.category} onChange={e => updateScheduleEntry(index, 'category', e.target.value)} className="h-9 px-3 bg-white border border-stone-200 rounded-xl text-sm font-body">
                        <option value="soin">🏥 Séance / Soin</option>
                        <option value="travail">💼 Travail</option>
                        <option value="sport">🏃 Sport</option>
                        <option value="créatif">🎨 Activité créative</option>
                        <option value="autre">🌟 Autre</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="time" value={entry.start_time} onChange={e => updateScheduleEntry(index, 'start_time', e.target.value)} className="h-9 px-3 bg-white border border-stone-200 rounded-xl text-sm font-body" />
                      <input type="time" value={entry.end_time} onChange={e => updateScheduleEntry(index, 'end_time', e.target.value)} className="h-9 px-3 bg-white border border-stone-200 rounded-xl text-sm font-body" />
                    </div>
                    <input type="text" value={entry.label} onChange={e => updateScheduleEntry(index, 'label', e.target.value)} placeholder="Ex: Séance orthophonie" className="w-full h-9 px-3 bg-white border border-stone-200 rounded-xl text-sm font-body" />
                    <div className="flex flex-wrap gap-1 pt-1">
                      <p className="text-xs text-slate-400 w-full">Dupliquer vers :</p>
                      {['lundi','mardi','mercredi','jeudi','vendredi','samedi','dimanche'].filter(d => d !== entry.day_of_week).map(day => (
                        <button key={day} type="button" onClick={() => duplicateScheduleEntry(index, [day])} className="text-xs px-2 py-1 bg-sage-50 text-sage-600 rounded-lg border border-sage-200 capitalize">{day.slice(0,3)}</button>
                      ))}
                      <button onClick={() => removeScheduleEntry(index)} className="ml-auto text-xs px-2 py-1 bg-red-50 text-red-400 rounded-lg border border-red-100">Supprimer</button>
                    </div>
                  </div>
                );
              })
            }
            <button onClick={addScheduleEntry} className="w-full py-3 border-2 border-dashed border-stone-200 rounded-2xl text-slate-400 font-heading font-semibold text-sm">+ Ajouter un créneau</button>
            <div className="flex gap-3">
              <button onClick={() => setEditingSchedule(false)} className="flex-1 h-11 bg-stone-100 text-slate-600 rounded-2xl font-heading font-semibold text-sm">Annuler</button>
              <button onClick={saveSchedule} disabled={saving} className="flex-1 h-11 text-white rounded-2xl font-heading font-semibold text-sm disabled:opacity-40" style={{ backgroundColor: '#4A9B8F' }}>{saving ? '...' : 'Enregistrer'}</button>
            </div>
          </div>
        )}
        {!editingSchedule && weeklySchedule.length > 0 && (
          <div className="mt-4 border-t border-stone-50 pt-3 space-y-2">
            {[...weeklySchedule].sort((a,b) => { const o = ['lundi','mardi','mercredi','jeudi','vendredi','samedi','dimanche']; return o.indexOf(a.day_of_week) - o.indexOf(b.day_of_week); }).map((item, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2 bg-stone-50 rounded-xl">
                <span className="text-xs font-heading font-semibold text-slate-500 w-16 capitalize">{item.day_of_week.slice(0,3)}</span>
                <span className="text-xs text-slate-400 font-body">{item.start_time?.slice(0,5)} – {item.end_time?.slice(0,5)}</span>
                <span className="text-xs font-body text-slate-600 flex-1 truncate">{item.label}</span>
              </div>
            ))}
            <button onClick={() => { setScheduleEdit([...weeklySchedule]); setEditingSchedule(true); }} className="text-sage-600 font-heading font-semibold text-sm mt-2">Modifier l'emploi du temps</button>
          </div>
        )}
        {!editingSchedule && weeklySchedule.length === 0 && (
          <div className="mt-4 border-t border-stone-50 pt-3 text-center">
            <p className="text-sm text-slate-400 font-body italic mb-2">Aucun créneau</p>
            <button onClick={() => { setScheduleEdit([]); setEditingSchedule(true); }} className="text-sage-600 font-heading font-semibold text-sm">+ Ajouter des créneaux</button>
          </div>
        )}
      </div>

      {/* Traitements */}
      <Section title="Traitements en cours" icon="💊"
        editing={editingTreatments} onEdit={() => { setTreatmentsEdit(treatments.map(t => ({...t}))); setEditingTreatments(true); }}
        onSave={saveTreatments} onCancel={() => setEditingTreatments(false)} saving={saving}>
        {editingTreatments ? (
          <div className="space-y-3">
            {treatmentsEdit.length === 0
              ? <p className="text-sm text-slate-400 font-body italic text-center py-2">Aucun traitement</p>
              : treatmentsEdit.map((t, index) => (
                <div key={t.id} className="p-3 bg-stone-50 rounded-2xl space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-heading font-semibold text-slate-500">Traitement {index + 1}</p>
                    <button onClick={() => removeTreatment(index)} className="w-6 h-6 rounded-full bg-red-50 flex items-center justify-center"><Trash2 size={12} className="text-red-400" /></button>
                  </div>
                  {[
                    { key: 'medication_name', placeholder: 'Nom du médicament *' },
                    { key: 'dosage', placeholder: 'Dosage (ex: 10mg)' },
                    { key: 'frequency', placeholder: 'Fréquence (ex: 2x/jour)' },
                    { key: 'prescriber', placeholder: 'Médecin prescripteur' },
                  ].map(({ key, placeholder }) => (
                    <input key={key} type="text" value={t[key] || ''} onChange={e => updateTreatment(index, key, e.target.value)} placeholder={placeholder} className="w-full h-9 px-3 bg-white border border-stone-200 rounded-xl text-sm font-body" />
                  ))}
                </div>
              ))
            }
            <button onClick={addTreatment} className="w-full py-3 border-2 border-dashed border-stone-200 rounded-2xl text-slate-400 font-heading font-semibold text-sm flex items-center justify-center gap-2"><Plus size={15} /> Ajouter un traitement</button>
          </div>
        ) : (
          treatments.length === 0
            ? <p className="text-sm text-slate-300 italic font-body py-2">Aucun traitement renseigné</p>
            : <div className="space-y-3">
              {treatments.map(t => (
                <div key={t.id} className="p-3 bg-stone-50 rounded-2xl">
                  <p className="font-heading font-semibold text-sm text-slate-700">{t.medication_name}</p>
                  {t.dosage && <p className="text-xs text-slate-400 font-body mt-0.5">Dosage : {t.dosage}</p>}
                  {t.frequency && <p className="text-xs text-slate-400 font-body">Fréquence : {t.frequency}</p>}
                  {t.prescriber && <p className="text-xs text-slate-400 font-body">Prescrit par : {t.prescriber}</p>}
                </div>
              ))}
            </div>
        )}
      </Section>

      {/* Équipe paramédicale */}
      <Section title="Équipe paramédicale" icon="🏥"
        editing={editingMedPros} onEdit={() => { setMedProsEdit(medPros.map(p => ({...p}))); setEditingMedPros(true); }}
        onSave={saveMedPros} onCancel={() => setEditingMedPros(false)} saving={saving}>
        {editingMedPros ? (
          <div className="space-y-3">
            {medProsEdit.length === 0
              ? <p className="text-sm text-slate-400 font-body italic text-center py-2">Aucun professionnel</p>
              : medProsEdit.map((pro, index) => (
                <div key={pro.id} className="p-3 bg-stone-50 rounded-2xl space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-heading font-semibold text-slate-500">{pro.profession || `Professionnel ${index + 1}`}</p>
                    <button onClick={() => removeMedPro(index)} className="w-6 h-6 rounded-full bg-red-50 flex items-center justify-center"><Trash2 size={12} className="text-red-400" /></button>
                  </div>
                  <select value={PRO_TYPES.some(t => t.label === pro.profession) ? pro.profession : pro.profession ? '__autre__' : ''} onChange={e => updateMedPro(index, 'profession', e.target.value === '__autre__' ? '' : e.target.value)} className="w-full h-9 px-3 bg-white border border-stone-200 rounded-xl text-sm font-body">
                    <option value="">Choisir la spécialité</option>
                    {PRO_TYPES.filter(t => t.key !== 'other_professional').map(t => <option key={t.key} value={t.label}>{t.label}</option>)}
                    <option value="__autre__">Autre (préciser)</option>
                  </select>
                  {!PRO_TYPES.some(t => t.label === pro.profession) && (
                    <input type="text" value={pro.profession || ''} onChange={e => updateMedPro(index, 'profession', e.target.value)} placeholder="Précisez la spécialité" className="w-full h-9 px-3 bg-white border border-stone-200 rounded-xl text-sm font-body" />
                  )}
                  {[
                    { key: 'professional_name', placeholder: 'Nom du professionnel' },
                    { key: 'phone', placeholder: 'Téléphone' },
                    { key: 'address', placeholder: 'Adresse / Cabinet' },
                    { key: 'frequency', placeholder: 'Fréquence (ex: 1 séance/semaine)' },
                  ].map(({ key, placeholder }) => (
                    <input key={key} type="text" value={pro[key] || ''} onChange={e => updateMedPro(index, key, e.target.value)} placeholder={placeholder} className="w-full h-9 px-3 bg-white border border-stone-200 rounded-xl text-sm font-body" />
                  ))}
                </div>
              ))
            }
            <button onClick={addMedPro} className="w-full py-3 border-2 border-dashed border-stone-200 rounded-2xl text-slate-400 font-heading font-semibold text-sm flex items-center justify-center gap-2"><Plus size={15} /> Ajouter manuellement</button>
            <button onClick={() => navigate('/patient/professionnels')} className="w-full py-3 rounded-2xl font-heading font-semibold text-sm flex items-center justify-center gap-2" style={{ backgroundColor: '#EDE9FE', color: '#7C6BAE' }}>🔍 Rechercher sur Passerelle</button>
          </div>
        ) : (
          medPros.length === 0
            ? <p className="text-sm text-slate-300 italic font-body py-2">Aucun professionnel renseigné</p>
            : <div className="space-y-3">
              {medPros.map(pro => (
                <div key={pro.id} className="p-3 bg-stone-50 rounded-2xl">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-xs font-heading font-bold flex-shrink-0" style={{ backgroundColor: '#7C6BAE' }}>{pro.professional_name?.[0] || 'P'}</div>
                    <div>
                      <p className="font-heading font-semibold text-sm text-slate-700">{pro.professional_name || 'Non renseigné'}</p>
                      <p className="text-xs text-slate-400 font-body">{pro.profession}</p>
                    </div>
                  </div>
                  {pro.phone && <p className="text-xs text-slate-400 font-body mt-1">📞 {pro.phone}</p>}
                  {pro.address && <p className="text-xs text-slate-400 font-body">📍 {pro.address}</p>}
                  {pro.frequency && <p className="text-xs text-slate-400 font-body">🔄 {pro.frequency}</p>}
                </div>
              ))}
            </div>
        )}
      </Section>

      {/* Communication */}
      <Section title="Communication & profil" icon="💬"
        editing={editingCommunication} onEdit={() => { setCommunicationEdit({...communication}); setEditingCommunication(true); }}
        onSave={() => saveSection(communicationEdit, 'patient_communication_profile', setCommunication, setEditingCommunication)}
        onCancel={() => setEditingCommunication(false)} saving={saving}>
        <div className="py-2.5 border-b border-stone-50">
          <p className="text-xs text-slate-400 font-body mb-2">Mode de communication</p>
          {editingCommunication ? (
            <div className="flex gap-2 flex-wrap">
              {['verbal', 'non_verbal', 'alternatif'].map(opt => (
                <button key={opt} onClick={() => setCommunicationEdit(p => ({...p, communication_type: opt}))}
                  className={`px-3 py-1.5 rounded-full text-xs font-heading font-semibold border ${communicationEdit.communication_type === opt ? 'bg-sage-600 text-white border-sage-600' : 'bg-white text-slate-600 border-stone-200'}`}>
                  {opt === 'verbal' ? 'Verbal' : opt === 'non_verbal' ? 'Non verbal' : 'Alternatif'}
                </button>
              ))}
            </div>
          ) : (
            <span className="text-sm font-heading font-semibold px-3 py-1 bg-sage-50 text-sage-700 rounded-full">
              {communication.communication_type === 'verbal' ? 'Verbal' : communication.communication_type === 'non_verbal' ? 'Non verbal' : communication.communication_type ? 'Communication alternative' : <span className="text-slate-300 italic font-body font-normal">Non renseigné</span>}
            </span>
          )}
        </div>
        {[
          { key: 'alternative_communication_details', label: 'Détails communication alternative' },
          { key: 'comprehension_level', label: 'Niveau de compréhension', multiline: true },
        ].map(({ key, label, multiline }) => (
          <div key={key} className="py-2.5 border-b border-stone-50 last:border-0">
            <p className="text-xs text-slate-400 font-body mb-1">{label}</p>
            {editingCommunication
              ? multiline
                ? <textarea value={communicationEdit[key] || ''} onChange={e => setCommunicationEdit(p => ({...p, [key]: e.target.value}))} rows={3} className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-xl text-sm font-body resize-none" />
                : <input type="text" value={communicationEdit[key] || ''} onChange={e => setCommunicationEdit(p => ({...p, [key]: e.target.value}))} className="w-full h-9 px-3 bg-stone-50 border border-stone-200 rounded-xl text-sm font-body" />
              : <p className="text-sm text-slate-700 font-body">{communication[key] || <span className="text-slate-300 italic">Non renseigné</span>}</p>
            }
          </div>
        ))}
      </Section>

      {/* Contacts urgence */}
      <Section title="Contacts d'urgence" icon="🆘"
        editing={editingContacts} onEdit={() => { setContactsEdit({...contacts}); setEditingContacts(true); }}
        onSave={() => saveSection(contactsEdit, 'patient_emergency_contacts', setContacts, setEditingContacts)}
        onCancel={() => setEditingContacts(false)} saving={saving}>
        {[{prefix: 'contact1', label: 'Contact 1'}, {prefix: 'contact2', label: 'Contact 2'}].map(({ prefix, label }) => (
          <div key={prefix}>
            <p className="text-xs font-heading font-semibold text-slate-500 mt-2 mb-2">{label}</p>
            {[{ key: `${prefix}_name`, label: 'Nom complet' }, { key: `${prefix}_relation`, label: 'Lien' }, { key: `${prefix}_phone`, label: 'Téléphone' }].map(({ key, label: lbl }) => (
              <div key={key} className="py-2.5 border-b border-stone-50 last:border-0">
                <p className="text-xs text-slate-400 font-body mb-1">{lbl}</p>
                {editingContacts
                  ? <input type="text" value={contactsEdit[key] || ''} onChange={e => setContactsEdit(p => ({...p, [key]: e.target.value}))} className="w-full h-9 px-3 bg-stone-50 border border-stone-200 rounded-xl text-sm font-body" />
                  : <p className="text-sm text-slate-700 font-body">{contacts[key] || <span className="text-slate-300 italic">Non renseigné</span>}</p>
                }
              </div>
            ))}
          </div>
        ))}
      </Section>

      {/* Infos complémentaires */}
      <Section title="Informations complémentaires" icon="📝"
        editing={editingAdditionalInfo} onEdit={() => { setAdditionalInfoEdit({...additionalInfo}); setEditingAdditionalInfo(true); }}
        onSave={() => saveSection(additionalInfoEdit, 'patient_additional_info', setAdditionalInfo, setEditingAdditionalInfo)}
        onCancel={() => setEditingAdditionalInfo(false)} saving={saving}>
        {[
          { key: 'interests', label: "Intérêts & centres d'intérêt" },
          { key: 'passions', label: 'Passions' },
          { key: 'allergies', label: 'Alimentation / allergies' },
          { key: 'habits', label: 'Habitudes / rituels' },
          { key: 'free_notes', label: 'Notes libres' },
        ].map(({ key, label }) => (
          <div key={key} className="py-2.5 border-b border-stone-50 last:border-0">
            <p className="text-xs text-slate-400 font-body mb-1">{label}</p>
            {editingAdditionalInfo
              ? <textarea value={additionalInfoEdit[key] || ''} onChange={e => setAdditionalInfoEdit(p => ({...p, [key]: e.target.value}))} rows={3} className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-xl text-sm font-body resize-none" />
              : <p className="text-sm text-slate-700 font-body">{additionalInfo[key] || <span className="text-slate-300 italic">Non renseigné</span>}</p>
            }
          </div>
        ))}
      </Section>

      {/* Mon compte */}
      <div className="passerelle-card">
        <div className="flex items-center gap-2 mb-3"><span className="text-base">🔒</span><h3 className="font-heading font-semibold text-slate-700 text-base">Mon compte</h3></div>
        <div className="py-2.5">
          <p className="text-xs text-slate-400 font-body mb-1">Email</p>
          <p className="text-sm font-heading font-semibold text-slate-700">{user?.email}</p>
        </div>
      </div>

      <button onClick={handleLogout} className="w-full h-12 border-2 border-red-100 text-red-400 rounded-2xl font-heading font-semibold flex items-center justify-center gap-2 hover:bg-red-50">
        <LogOut size={16} /> Se déconnecter
      </button>
      <div className="h-4" />
    </div>
  );
}
